import { TranslateService } from "@ngx-translate/core";
import { getMoBaseLanguage } from "../payment-gateways/payment-method-label.helpers";

export const dineinTableAlternativeNameField = "dinein_table_alternative_name";

export interface DineinTableLabelContext {
  word: string | null;
  selectedLanguage: string | null;
}

// Default i18n key -> the wording used when the store renamed "table".
//
// An empty string means the surface renders the bare noun, so the configured
// word is returned as-is. Any key that is NOT in this map keeps its existing
// translation untouched, which is what lets the pipe wrap generic message keys
// (e.g. `deliveryMethodErrMsg`) without affecting unrelated messages.
export const dineinTableCustomKeys: { [defaultKey: string]: string } = {
  "popups.dine-in-modal.table": "",
  "orders.table": "",
  "orders.order-item.table": "",
  "popups.send-order-popup.table": "",
  "popups.send-order-and-pay-popup.table": "",

  "popups.dine-in-modal.select-table": "dineinTableCustom.select",
  "popups.order-now-popup.select-table": "dineinTableCustom.select",
  "catalog.cart-catalog.choose-a-table": "dineinTableCustom.choose",
  "errorMessages.please_select_a_dinein_table":
    "dineinTableCustom.choose-required",
  "delivery-method-address.please-confirm-you-table":
    "dineinTableCustom.confirm",
  "popups.dine-in-modal.table-not-found": "dineinTableCustom.not-found",
  dinein_table_not_found: "dineinTableCustom.not-found-retry",
  open_camera_and_scan_table_qr: "dineinTableCustom.scan-qr",
  scan_table_qr_to_continue: "dineinTableCustom.scan-qr-inline",
  "errorMessages.dinein_table_recognition_failed_disable_adblock":
    "dineinTableCustom.recognition-failed",
  "errorMessages.not-available-for-this-table":
    "dineinTableCustom.payment-not-available",
  "the-selected-payment-method-is-not-available-for-this-table":
    "dineinTableCustom.payment-not-available",
  "cart.cart-components.payment-methods.not-available-for-this-table":
    "dineinTableCustom.payment-not-available-short",
  "do-you-want-to-continue-your-current-dine-in-order-at-table":
    "dineinTableCustom.continue-order-at",
  "errorMessages.visitor_dinein_not_enabled":
    "dineinTableCustom.ordering-not-enabled",
  "popups.magic-login-confirmation.guest-order-message":
    "dineinTableCustom.guest-scan-qr",
  "popups.magic-login-confirmation.continue-as-guest-on-selected-table":
    "dineinTableCustom.continue-guest-selected",
};

function normalizeWord(value: any): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const word = value.trim();
  return word ? word : null;
}

// Resolves `store_info.dinein_table_alternative_name` the same way
// `resolveMobileorderPaymentLabel` resolves a payment label: selected language
// first, then the MobileOrder base language, then el/en, then any non-empty
// entry. Anything that is not a usable object or string resolves to null, which
// keeps every default translation in place.
export function resolveDineinTableWord(
  storeInfo: any,
  selectedLanguage?: string,
  baseLanguage?: string
): string | null {
  const value = storeInfo && storeInfo[dineinTableAlternativeNameField];

  const directWord = normalizeWord(value);
  if (directWord) {
    return directWord;
  }

  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const preferredLanguages = [selectedLanguage, baseLanguage, "el", "en"].filter(
    (language) => !!language
  );

  for (const language of preferredLanguages) {
    const localizedWord = normalizeWord(value[language]);
    if (localizedWord) {
      return localizedWord;
    }
  }

  for (const language of Object.keys(value)) {
    const localizedWord = normalizeWord(value[language]);
    if (localizedWord) {
      return localizedWord;
    }
  }

  return null;
}

export function resolveDineinTableWordFromState(
  storeInfo: any,
  groupApp: any,
  selectedLanguage?: string
): string | null {
  return resolveDineinTableWord(
    storeInfo,
    selectedLanguage,
    getMoBaseLanguage(groupApp)
  );
}

// `translate.use()` sets `currentLang` synchronously but fetches the locale file
// asynchronously, nothing in this app ever reaches `setDefaultLang()` (its only
// call site, `TranslateConfigService.getDefaultLanguage()`, has no callers) and
// `TranslateModule.forRoot()` registers neither `defaultLanguage` nor a
// `MissingTranslationHandler`. So while the file is in flight `instant()` hands
// back the key verbatim.
function localeNotLoadedYet(translateService: TranslateService): boolean {
  const translations = translateService.translations;
  const lang = translateService.currentLang || translateService.defaultLang;
  return !!translations && !!lang && !translations[lang];
}

// Returns the text to render for `defaultKey`. Without a configured word - or
// with a custom key that is missing from the active locale - it returns exactly
// what the app renders today.
//
// `blankWhileLoading` is passed by the pipe only. The template call sites it
// replaced used `| translate`, which is impure and renders "" until the locale
// file lands, so the pipe has to render "" too - the service republishes on
// `onLangChange`, which re-runs it with the real text. `DineinTableLabelService
// .instant()` deliberately leaves it false: the `translateService.instant()`
// calls it replaced already returned the raw key in that window.
export function resolveDineinTableLabel(
  translateService: TranslateService,
  defaultKey: string,
  tableWord?: string | null,
  blankWhileLoading = false
): string {
  // TranslatePipe returns a falsy/empty key unchanged instead of throwing, and
  // some call sites pipe a value that can be undefined. Match that exactly.
  if (!defaultKey) {
    return defaultKey;
  }

  // Only the lookups go through the guard. A configured word is rendered as-is
  // on the bare-noun surfaces, because it needs no locale file at all.
  const instant = (key: string, params?: any): string =>
    blankWhileLoading && localeNotLoadedYet(translateService)
      ? ""
      : translateService.instant(key, params);

  const word = normalizeWord(tableWord);
  if (!word) {
    return instant(defaultKey);
  }

  const customKey = dineinTableCustomKeys[defaultKey];
  if (customKey === undefined) {
    return instant(defaultKey);
  }

  if (customKey === "") {
    return word;
  }

  // No MissingTranslationHandler is configured, so an unresolved key reaches the
  // customer verbatim - fall back to the default wording instead.
  const translated = instant(customKey, { table: word });
  return translated && translated !== customKey
    ? translated
    : instant(defaultKey);
}
